import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

export type CustomerRateBulkValidationIssue = {
  row: number
  field: string
  errorType: string
  message: string
}

export type CustomerRateBulkValidationRow = {
  row: number
  data: Record<string, string>
  isValid: boolean
  invalidFields: string[]
}

export type CustomerRateBulkValidationResult = {
  errorCount: number
  validRowCount: number
  totalRowCount: number
  isValid: boolean
  headers: string[]
  rows: CustomerRateBulkValidationRow[]
  issues: CustomerRateBulkValidationIssue[]
}

export type CustomerRateBulkCreateResult = {
  createdCount: number
  failed: string[]
}

type ApiMessage<T> = {
  status: string
  text: string
  result: T
}

const getValue = (obj: any, camel: string, pascal: string) => obj?.[camel] ?? obj?.[pascal]

const normalizeValidationResult = (result: any): CustomerRateBulkValidationResult => {
  const errorCount = getValue(result, 'errorCount', 'ErrorCount') ?? 0
  const validRowCount = getValue(result, 'validRowCount', 'ValidRowCount') ?? 0
  const previewRowsRaw =
    getValue(result, 'previewRows', 'PreviewRows') ??
    getValue(result, 'rows', 'Rows') ??
    []

  return {
    errorCount,
    validRowCount,
    totalRowCount: getValue(result, 'totalRowCount', 'TotalRowCount') ?? 0,
    isValid:
      getValue(result, 'isValid', 'IsValid') ?? (errorCount === 0 && validRowCount > 0),
    headers: getValue(result, 'headers', 'Headers') ?? [],
    rows: previewRowsRaw.map((row: any) => ({
      row: getValue(row, 'row', 'Row') ?? 0,
      data: getValue(row, 'data', 'Data') ?? {},
      isValid: getValue(row, 'isValid', 'IsValid') ?? false,
      invalidFields: getValue(row, 'invalidFields', 'InvalidFields') ?? [],
    })),
    issues: (getValue(result, 'issues', 'Issues') ?? []).map((issue: any) => ({
      row: getValue(issue, 'row', 'Row') ?? 0,
      field: getValue(issue, 'field', 'Field') ?? '',
      errorType: getValue(issue, 'errorType', 'ErrorType') ?? '',
      message: getValue(issue, 'message', 'Message') ?? '',
    })),
  }
}

const normalizeCreateResult = (result: any): CustomerRateBulkCreateResult => ({
  createdCount: getValue(result, 'createdCount', 'CreatedCount') ?? 0,
  failed: getValue(result, 'failed', 'Failed') ?? [],
})

export const downloadCustomerRateBulkTemplate = async (customerId: string): Promise<void> => {
  const response = await axios.get(`${API_URL}/rates/customers/${customerId}/bulk/template`, {
    responseType: 'blob',
  })
  const blob = new Blob([response.data], {type: 'text/csv'})
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'customer_rates_bulk_template.csv'
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const validateCustomerRateBulkUpload = async (
  customerId: string,
  file: File
): Promise<CustomerRateBulkValidationResult> => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await axios.post<ApiMessage<any>>(
    `${API_URL}/rates/customers/${customerId}/bulk/validate`,
    formData,
    {
      headers: {'Content-Type': 'multipart/form-data'},
      params: {_t: Date.now()},
    }
  )
  return normalizeValidationResult(getValue(response.data, 'result', 'Result'))
}

export const createCustomerRateBulkUpload = async (
  customerId: string,
  file: File
): Promise<{
  create: CustomerRateBulkCreateResult | null
  text: string
  status: string
}> => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await axios.post<ApiMessage<any>>(
    `${API_URL}/rates/customers/${customerId}/bulk/create`,
    formData,
    {
      headers: {'Content-Type': 'multipart/form-data'},
    }
  )
  return {
    status: getValue(response.data, 'status', 'Status') ?? 'E',
    text: getValue(response.data, 'text', 'Text') ?? '',
    create: normalizeCreateResult(getValue(response.data, 'result', 'Result')),
  }
}
